import type en from '../en/templates'
import type { Translation } from '../types'

/** Configuration templates: the list, the wizard and the blueprints it renders */
const templates: Translation<typeof en> = {
  "templates.title": "Modèles",
  "templates.subtitle": "Configuration réutilisable pour vos sites",
  "templates.new": "Nouveau modèle",
  "templates.allTypes": "Tous les types",
  "templates.empty": "Aucun modèle pour l’instant",
  "templates.emptyText": "Créez un modèle en récupérant la configuration d’un site existant, ou construisez-en un vous-même.",
  "templates.createFirst": "Créer le premier modèle",
  "templates.family.ANY": "Tout appareil",
  "templates.defaultBadge": "PAR DÉFAUT",
  "templates.origin.PULLED": "récupéré",
  "templates.origin.MANUAL": "construit",
  "templates.system": "Système",
  "templates.applications": { one: "appliqué {count} fois", other: "appliqué {count} fois" },
  "templates.editHint": "Modifier le modèle et sa configuration",
  "templates.apply": "Appliquer",
  "templates.deleteNamed": "Supprimer {name}",
  "templates.deleteConfirm": "Supprimer le modèle « {name} » ?",
  "templates.applyTitle": "Appliquer le modèle",
  "templates.queued": { one: "En file d’attente pour {count} site", other: "En file d’attente pour {count} sites" },
  "templates.queuedText": "Chaque site indique les champs appliqués. Les champs non pris en charge sont consignés, pas ignorés en silence.",
  "templates.done": "Terminé",
  "templates.targets": "« {name} » est destiné aux appareils {family}.",
  "templates.noCompatible": "Aucun site connecté ne peut recevoir ce modèle. Vérifiez la famille d’appareils à laquelle il est destiné.",
  "templates.partial": { one: "{count} site choisi ignorera certains champs :", other: "{count} sites choisis ignoreront certains champs :" },
  "templates.more": "+{count} de plus",
  "templates.showIncompatible": { one: "Afficher {count} site qui ne peut pas le recevoir", other: "Afficher {count} sites qui ne peuvent pas le recevoir" },
  "templates.hideIncompatible": { one: "Masquer {count} site qui ne peut pas le recevoir", other: "Masquer {count} sites qui ne peuvent pas le recevoir" },
  "templates.notLicensed": "hors de votre licence",
  "templates.noSupported": "aucun champ pris en charge",
  "templates.wrongFamily": "autre famille d’appareils",
  "templates.applyTo": { one: "Appliquer à {count} site", other: "Appliquer à {count} sites" },
  "templates.allSupported": "Tous les champs sont pris en charge",
  "templates.skipped": { one: "{count} champ sera ignoré", other: "{count} champs seront ignorés" },
  "templates.family.UNIFI": "Contrôleurs et Site Manager",
  "templates.family.VELANOX": "WAN agrégé One Speed",
  "templates.family.TELTONIKA": "Routeurs cellulaires RutOS",
  "templates.family.PEPLINK": "Balance / MAX avec SpeedFusion",
  "templates.family.MIKROTIK": "Routeurs RouterOS et liaisons radio PtP / PtMP",
  "templates.saveFailed": "Le modèle n’a pas pu être enregistré",
  "templates.pullFailed": "La configuration n’a pas pu être récupérée",
  "templates.updated": "Modèle mis à jour",
  "templates.saved": "Modèle enregistré",
  "templates.viewAll": "Voir les modèles",
  "templates.createAnother": "En créer un autre",
  "templates.back": "Retour",
  "templates.editing": "Modifier : {name}",
  "templates.whichKind": "Pour quel type d’appareil ?",
  "templates.sitesConnected": { one: "{count} site connecté", other: "{count} sites connectés" },
  "templates.named": "Modèle {name}",
  "templates.whatConfigure": "Que souhaitez-vous configurer ?",
  "templates.variant": "Variante cible",
  "templates.variantHint": "Les variantes gérées par le cloud prennent en charge moins de modifications que l’accès direct à l’appareil.",
  "templates.loadingTypes": "Chargement des types de modèles…",
  "templates.howCreate": "Comment souhaitez-vous le créer ?",
  "templates.buildWizard": "Construire avec l’assistant",
  "templates.buildHint": "Étape par étape, en ne montrant que ce que les appareils {family} prennent en charge.",
  "templates.pullFromSite": "Récupérer depuis un site",
  "templates.noPullSites": "Aucun site avec contrôleur UniFi connecté pour l’instant.",
  "templates.pullOnlyFor": "Disponible pour les modèles réseau, Wi-Fi et WAN depuis un site avec contrôleur UniFi.",
  "templates.pullFrom": { one: "Copiez la configuration en vigueur de votre site avec contrôleur UniFi.", other: "Copiez la configuration en vigueur de l’un de vos {count} sites avec contrôleur UniFi." },
  "templates.sourceSite": "Site source *",
  "templates.chooseSite": "Choisissez un site…",
  "templates.name": "Nom du modèle *",
  "templates.description": "Description",
  "templates.optional": "Facultatif",
  "templates.optionalParen": "(facultatif)",
  "templates.pullConfig": "Récupérer la configuration",
  "templates.coverage": "{provider} prend en charge {count} des {total} options de ce type de modèle.",
  "templates.hiddenSteps": { one: "{count} étape est entièrement masquée.", other: "{count} étapes sont entièrement masquées." },
  "templates.stepOf": "Étape {n} sur {total}",
  "templates.nothingHere": "Rien à configurer à cette étape pour {provider}.",
  "templates.showUnsupported": { one: "Afficher {count} option non prise en charge", other: "Afficher {count} options non prises en charge" },
  "templates.hideUnsupported": { one: "Masquer {count} option non prise en charge", other: "Masquer {count} options non prises en charge" },
  "templates.saveTemplate": "Enregistrer le modèle",
  "templates.updateTemplate": "Mettre à jour le modèle",
  "templates.myName": "Mon {name}",
  "templates.useSuggestion": "Utiliser « {name} »",
  "templates.next": "Suivant",
  "templates.notSupported": "non pris en charge",
  "templates.enabled": "Activé",
  "templates.disabled": "Désactivé",
  "templates.choose": "Choisissez…",
  "templates.typeEnter": "Saisissez puis appuyez sur Entrée",
  "templates.add": "Ajouter",
  "templates.removeNamed": "Retirer {name}",
  "templates.inUse": "utilisés : {list}",
  "templates.type.NETWORK": "Réseau / VLAN",
  "templates.typeDesc.NETWORK": "Segmentation VLAN, plage DHCP et isolation",
  "templates.step.NETWORK.identity": "Usage et identité",
  "templates.stepDesc.NETWORK.identity": "À quoi sert ce réseau",
  "templates.field.NETWORK.purpose": "Usage",
  "templates.option.NETWORK.purpose.production": "🏭 Production",
  "templates.option.NETWORK.purpose.cash-registers": "🏪 Caisses",
  "templates.option.NETWORK.purpose.entrance": "🚪 Entrée / hall",
  "templates.option.NETWORK.purpose.cp": "🖥️ CP / panneau de commande",
  "templates.option.NETWORK.purpose.employers": "👔 Employés / personnel",
  "templates.option.NETWORK.purpose.media": "📺 Médias / AV",
  "templates.option.NETWORK.purpose.iot": "📡 Appareils IoT",
  "templates.option.NETWORK.purpose.guest": "🌐 Invités / visiteurs",
  "templates.option.NETWORK.purpose.vlan-only": "🔒 VLAN uniquement (sans DHCP)",
  "templates.field.NETWORK.name": "Nom du réseau",
  "templates.field.NETWORK.vlanId": "VLAN ID",
  "templates.help.NETWORK.vlanId": "Proposé d’après les VLAN déjà utilisés par vos modèles",
  "templates.step.NETWORK.addressing": "Adressage IP et DHCP",
  "templates.stepDesc.NETWORK.addressing": "Sous-réseau et plage d’adresses",
  "templates.field.NETWORK.subnet": "Sous-réseau",
  "templates.help.NETWORK.subnet": "p. ex. 192.168.30.0/24",
  "templates.field.NETWORK.dhcp.enabled": "Serveur DHCP",
  "templates.field.NETWORK.dhcp.start": "Début DHCP",
  "templates.field.NETWORK.dhcp.end": "Fin DHCP",
  "templates.field.NETWORK.dhcp.dns": "Serveurs DNS",
  "templates.step.NETWORK.security": "Sécurité",
  "templates.stepDesc.NETWORK.security": "Isolation et protection",
  "templates.field.NETWORK.isolation": "Isolation des clients",
  "templates.help.NETWORK.isolation": "Empêche les appareils de ce réseau de communiquer entre eux",
  "templates.field.NETWORK.dhcpGuard": "Protection DHCP",
  "templates.help.NETWORK.dhcpGuard": "Bloque les serveurs DHCP non autorisés",
  "templates.field.NETWORK.igmpSnooping": "IGMP snooping",
  "templates.type.WIFI": "Réseau Wi-Fi",
  "templates.typeDesc.WIFI": "SSID, sécurité et comportement radio",
  "templates.step.WIFI.identity": "SSID et identité",
  "templates.stepDesc.WIFI.identity": "Nom et visibilité",
  "templates.field.WIFI.ssid": "SSID",
  "templates.field.WIFI.enabled": "Réseau actif",
  "templates.field.WIFI.isHidden": "SSID masqué",
  "templates.field.WIFI.isGuest": "Réseau invité",
  "templates.help.WIFI.isGuest": "Isole les clients du LAN",
  "templates.step.WIFI.security": "Sécurité",
  "templates.stepDesc.WIFI.security": "Chiffrement et identifiants",
  "templates.field.WIFI.security": "Type de sécurité",
  "templates.option.WIFI.security.wpa3": "WPA3-Personnel (recommandé)",
  "templates.option.WIFI.security.wpa2": "WPA2-Personnel (compatible)",
  "templates.option.WIFI.security.wpa2-enterprise": "WPA2-Entreprise (RADIUS)",
  "templates.option.WIFI.security.open": "Ouvert — sans mot de passe",
  "templates.field.WIFI.passphrase": "Mot de passe",
  "templates.help.WIFI.passphrase": "8 caractères minimum",
  "templates.field.WIFI.vlanId": "Étiquette VLAN",
  "templates.help.WIFI.vlanId": "Vide : le SSID reste sans étiquette",
  "templates.step.WIFI.radio": "Radio et performances",
  "templates.stepDesc.WIFI.radio": "Bandes et itinérance",
  "templates.field.WIFI.band": "Bande",
  "templates.option.WIFI.band.both": "2.4 + 5 GHz",
  "templates.option.WIFI.band.5g": "5 GHz uniquement",
  "templates.option.WIFI.band.2g": "2,4 GHz uniquement",
  "templates.option.WIFI.band.6g": "6 GHz (Wi-Fi 6E)",
  "templates.field.WIFI.txPower": "Puissance d’émission",
  "templates.option.WIFI.txPower.auto": "Automatique",
  "templates.option.WIFI.txPower.low": "Faible",
  "templates.option.WIFI.txPower.medium": "Moyenne",
  "templates.option.WIFI.txPower.high": "Élevée",
  "templates.field.WIFI.fastRoaming": "Itinérance rapide (802.11r)",
  "templates.field.WIFI.minRssi": "Signal minimal",
  "templates.field.WIFI.scheduleEnabled": "Planification activée",
  "templates.type.WAN": "WAN / Internet",
  "templates.typeDesc.WAN": "Comportement des liaisons montantes, basculement et répartition de charge",
  "templates.step.WAN.links": "Comportement des liaisons",
  "templates.stepDesc.WAN.links": "Comment les liaisons WAN sont utilisées",
  "templates.field.WAN.failoverEnabled": "Basculement",
  "templates.help.WAN.failoverEnabled": "Basculer vers une liaison de secours en cas de panne de la principale",
  "templates.field.WAN.loadBalancingEnabled": "Répartition de charge",
  "templates.help.WAN.loadBalancingEnabled": "Répartit les sessions sur les liaisons actives",
  "templates.field.WAN.wanPortCount": "Nombre de liaisons WAN",
  "templates.option.WAN.wanPortCount.2": "2",
  "templates.option.WAN.wanPortCount.3": "3",
  "templates.option.WAN.wanPortCount.4": "4",
  "templates.field.WAN.healthCheckEnabled": "Contrôle de santé",
  "templates.help.WAN.healthCheckEnabled": "Teste chaque liaison et la marque en panne en cas d’échec",
  "templates.field.WAN.priorityMode": "Mode de priorité",
  "templates.option.WAN.priorityMode.ordered": "Ordre strict",
  "templates.option.WAN.priorityMode.weighted": "Pondéré",
  "templates.step.WAN.quality": "Qualité de service",
  "templates.stepDesc.WAN.quality": "Gestion des files d’attente et MTU",
  "templates.field.WAN.smartQueueEnabled": "Smart Queue (QoS)",
  "templates.help.WAN.smartQueueEnabled": "Réduit le bufferbloat sous charge",
  "templates.field.WAN.smartQueueAutoDetect": "Détecter automatiquement le débit au déploiement",
  "templates.help.WAN.smartQueueAutoDetect": "Lance un test de débit à l’application et utilise 90 % du résultat",
  "templates.field.WAN.smartQueueDown": "Débit descendant",
  "templates.field.WAN.smartQueueUp": "Débit montant",
  "templates.field.WAN.mtu": "MTU",
  "templates.type.CELLULAR": "Cellulaire / SIM",
  "templates.typeDesc.CELLULAR": "APN, politique SIM, plafonds de données et itinérance",
  "templates.step.CELLULAR.carrier": "Opérateur et APN",
  "templates.stepDesc.CELLULAR.carrier": "Comment le modem s’attache au réseau",
  "templates.field.CELLULAR.apn": "APN",
  "templates.help.CELLULAR.apn": "Laisser vide pour la détection automatique par l’opérateur",
  "templates.field.CELLULAR.apnUser": "Nom d’utilisateur APN",
  "templates.field.CELLULAR.apnPassword": "Mot de passe APN",
  "templates.field.CELLULAR.pin": "Code PIN de la SIM",
  "templates.step.CELLULAR.sim": "Politique SIM",
  "templates.stepDesc.CELLULAR.sim": "Quelle SIM est utilisée et quand basculer",
  "templates.field.CELLULAR.simSwitch": "Emplacement SIM préféré",
  "templates.option.CELLULAR.simSwitch.1": "SIM 1",
  "templates.option.CELLULAR.simSwitch.2": "SIM 2",
  "templates.field.CELLULAR.simFailover": "Basculement automatique en cas de panne",
  "templates.help.CELLULAR.simFailover": "Bascule sur l’autre SIM si la connexion tombe",
  "templates.field.CELLULAR.roaming": "Autoriser l’itinérance",
  "templates.help.CELLULAR.roaming": "Peut être coûteux hors du réseau d’origine",
  "templates.step.CELLULAR.limits": "Données et radio",
  "templates.stepDesc.CELLULAR.limits": "Plafonds et choix des bandes",
  "templates.field.CELLULAR.dataLimit": "Plafond de données mensuel",
  "templates.help.CELLULAR.dataLimit": "Une alerte est émise à 90 % de ce volume",
  "templates.field.CELLULAR.dataLimitAction": "Une fois le plafond atteint",
  "templates.option.CELLULAR.dataLimitAction.alert": "Alerte uniquement",
  "templates.option.CELLULAR.dataLimitAction.throttle": "Limiter le débit",
  "templates.option.CELLULAR.dataLimitAction.disconnect": "Déconnecter",
  "templates.field.CELLULAR.bandLock": "Verrouillage de bande",
  "templates.help.CELLULAR.bandLock": "Numéros de bande séparés par des virgules, vide = automatique",
  "templates.field.CELLULAR.networkMode": "Mode réseau",
  "templates.option.CELLULAR.networkMode.auto": "Automatique",
  "templates.option.CELLULAR.networkMode.5g": "5G de préférence",
  "templates.option.CELLULAR.networkMode.lte": "LTE uniquement",
  "templates.option.CELLULAR.networkMode.3g": "3G en secours",
  "templates.type.TUNNEL": "Tunnel / agrégation",
  "templates.typeDesc.TUNNEL": "Agrégation SpeedFusion et profils de client VPN",
  "templates.step.TUNNEL.profile": "Profil du tunnel",
  "templates.stepDesc.TUNNEL.profile": "Pair et mode d’agrégation",
  "templates.field.TUNNEL.name": "Nom du profil",
  "templates.field.TUNNEL.peerHost": "Hôte du pair",
  "templates.help.TUNNEL.peerHost": "Nom d’hôte ou adresse IP de l’extrémité du tunnel",
  "templates.field.TUNNEL.mode": "Mode",
  "templates.option.TUNNEL.mode.bonding": "Agrégation — cumuler la bande passante",
  "templates.option.TUNNEL.mode.failover": "Basculement — secours à chaud",
  "templates.option.TUNNEL.mode.overflow": "Débordement — vers la liaison secondaire",
  "templates.field.TUNNEL.encryption": "Chiffrement",
  "templates.option.TUNNEL.encryption.aes256": "AES-256",
  "templates.option.TUNNEL.encryption.aes128": "AES-128",
  "templates.option.TUNNEL.encryption.none": "Aucun",
  "templates.step.TUNNEL.behaviour": "Comportement",
  "templates.stepDesc.TUNNEL.behaviour": "Reconnexion et client VPN",
  "templates.field.TUNNEL.autoReconnect": "Reconnexion automatique",
  "templates.field.TUNNEL.vpnClientEnabled": "Client VPN",
  "templates.field.TUNNEL.vpnClientConfig": "Profil VPN",
  "templates.type.SYSTEM": "Socle système",
  "templates.typeDesc.SYSTEM": "Heure, identité et politique de micrologiciel",
  "templates.step.SYSTEM.identity": "Identité et heure",
  "templates.stepDesc.SYSTEM.identity": "Nommage et horloge",
  "templates.field.SYSTEM.hostname": "Modèle de nom d’hôte",
  "templates.help.SYSTEM.hostname": "Utilisez {site} pour insérer le nom du site",
  "templates.field.SYSTEM.timezone": "Fuseau horaire",
  "templates.field.SYSTEM.ntp": "Serveur NTP",
  "templates.step.SYSTEM.maintenance": "Maintenance",
  "templates.stepDesc.SYSTEM.maintenance": "Micrologiciel et sauvegardes",
  "templates.field.SYSTEM.firmwareCheck": "Vérifier le micrologiciel à l’application",
  "templates.field.SYSTEM.firmwareUpgrade": "Installer les mises à jour automatiquement",
  "templates.help.SYSTEM.firmwareUpgrade": "Applique la dernière version — provoque un redémarrage",
  "templates.field.SYSTEM.backup": "Sauvegarder avant les modifications",
  "templates.type.LINK": "Liaison radio",
  "templates.typeDesc.LINK": "Paramètres radio point à point ou point à multipoint — un modèle par côté",
  "templates.step.LINK.role": "Rôle",
  "templates.stepDesc.LINK.role": "Quelle extrémité de la liaison est cet appareil",
  "templates.field.LINK.topology": "Topologie",
  "templates.option.LINK.topology.ptp": "Point à point — une station",
  "templates.option.LINK.topology.ptmp": "Point à multipoint — plusieurs stations",
  "templates.field.LINK.role": "Ce côté",
  "templates.help.LINK.role": "Appliquez d’abord le modèle de la station, puis celui du point d’accès dans les 10 minutes — dans l’autre sens, les stations perdent la liaison. Une station garde ses nouveaux paramètres dès que le point d’accès correspond ; sinon, elle revient d’elle-même en arrière",
  "templates.option.LINK.role.ap": "Point d’accès (le secteur / l’extrémité proche)",
  "templates.option.LINK.role.station": "Station (le CPE / l’extrémité distante)",
  "templates.field.LINK.interface": "Interface radio",
  "templates.help.LINK.interface": "Vide : la radio de liaison est choisie s’il y en a une (une station, une radio 60 GHz ou NV2/Nstreme) ; un appareil à plusieurs radios a besoin du nom (wlan60-1, wlan1, wifi1 …)",
  "templates.step.LINK.radio": "Radio",
  "templates.stepDesc.LINK.radio": "Les deux extrémités doivent correspondre",
  "templates.field.LINK.ssid": "Nom de la liaison (SSID)",
  "templates.field.LINK.linkKey": "Clé de la liaison",
  "templates.help.LINK.linkKey": "Au moins 8 caractères. Clé NV2, phrase secrète WPA2/WPA3 ou mot de passe 60 GHz, selon la radio",
  "templates.field.LINK.frequency": "Fréquence",
  "templates.help.LINK.frequency": "5 GHz p. ex. 5500. 60 GHz : 58320, 60480, 62640, 64800 ou 66000. Vide conserve l’actuelle",
  "templates.field.LINK.channelWidth": "Largeur de canal",
  "templates.help.LINK.channelWidth": "Non utilisé en 60 GHz",
  "templates.option.LINK.channelWidth.20": "20 MHz",
  "templates.option.LINK.channelWidth.40": "40 MHz",
  "templates.option.LINK.channelWidth.80": "80 MHz",
  "templates.field.LINK.protocol": "Protocole",
  "templates.help.LINK.protocol": "Uniquement le pilote « wireless » 2,4/5 GHz ; les radios Wi-Fi 6 utilisent toujours 802.11",
  "templates.option.LINK.protocol.nv2": "NV2 (TDMA MikroTik — idéal pour les liaisons extérieures)",
  "templates.option.LINK.protocol.802.11": "802.11 (Wi-Fi standard)",
  "templates.option.LINK.protocol.nstreme": "Nstreme",
  "templates.option.LINK.protocol.any": "Tous (suivre le point d’accès)",
  "templates.field.LINK.txPower": "Puissance d’émission",
  "templates.help.LINK.txPower": "Vide conserve le réglage actuel. Restez dans la limite de votre domaine réglementaire",
  "templates.type.CYBERSECURE": "CyberSecure",
  "templates.typeDesc.CYBERSECURE": "IPS, filtrage de contenu et gestion des menaces",
  "templates.step.CYBERSECURE.ips": "Prévention des intrusions",
  "templates.stepDesc.CYBERSECURE.ips": "Blocage par signatures",
  "templates.field.CYBERSECURE.ips.enabled": "Activer l’IPS/IDS",
  "templates.field.CYBERSECURE.ips.mode": "Mode",
  "templates.option.CYBERSECURE.ips.mode.ips": "IPS — bloquer les menaces",
  "templates.option.CYBERSECURE.ips.mode.ids": "IDS — alerter uniquement",
  "templates.step.CYBERSECURE.content": "Filtrage de contenu",
  "templates.stepDesc.CYBERSECURE.content": "Blocage par catégorie et domaine",
  "templates.field.CYBERSECURE.contentFilter.enabled": "Activer le filtrage de contenu",
  "templates.field.CYBERSECURE.contentFilter.categories": "Catégories bloquées",
  "templates.option.CYBERSECURE.contentFilter.categories.adult": "🔞 Adultes",
  "templates.option.CYBERSECURE.contentFilter.categories.gambling": "🎰 Jeux d’argent",
  "templates.option.CYBERSECURE.contentFilter.categories.malware": "🦠 Malware",
  "templates.option.CYBERSECURE.contentFilter.categories.p2p": "⬇️ P2P",
  "templates.option.CYBERSECURE.contentFilter.categories.ads": "📢 Publicités",
  "templates.option.CYBERSECURE.contentFilter.categories.social": "💬 Réseaux sociaux",
  "templates.option.CYBERSECURE.contentFilter.categories.streaming": "📺 Streaming",
  "templates.option.CYBERSECURE.contentFilter.categories.games": "🎮 Jeux",
  "templates.field.CYBERSECURE.contentFilter.customBlockedUrls": "Domaines bloqués personnalisés",
  "templates.help.CYBERSECURE.contentFilter.customBlockedUrls": "Ajoutez les domaines un par un",
  "templates.type.COMPANY_DEFAULTS": "Paramètres par défaut de l’entreprise",
  "templates.typeDesc.COMPANY_DEFAULTS": "Un ensemble de modèles appliqué à chaque nouveau site",
  "templates.step.COMPANY_DEFAULTS.bundle": "Modèles inclus",
  "templates.stepDesc.COMPANY_DEFAULTS.bundle": "Appliqués dans l’ordre lorsqu’un site passe en ligne",
  "templates.field.COMPANY_DEFAULTS.networkTemplateIds": "Modèles réseau",
  "templates.field.COMPANY_DEFAULTS.wifiTemplateIds": "Modèles Wi-Fi",
  "templates.field.COMPANY_DEFAULTS.wanTemplateId": "Modèle WAN",
  "templates.field.COMPANY_DEFAULTS.cellularTemplateId": "Modèle cellulaire",
  "templates.field.COMPANY_DEFAULTS.tunnelTemplateId": "Modèle de tunnel",
  "templates.field.COMPANY_DEFAULTS.systemTemplateId": "Modèle système",
}

export default templates
